import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Circle, CircleDot, ExternalLink, FileText, GitBranch, Globe, ListTodo, Settings, SkipForward } from 'lucide-react'
import { useProject } from './data'
import type { ProjectStages, StageAdvance, StageCheckSet } from '@one-deployment/api'
import { Button, SkeletonRows } from '@/components/ui'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Age, ErrorState } from '@/components/status'
import { useCan } from '@/components/RoleGate'
import { askText } from '@/components/ask'
import { enc, get, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'

/**
 * The project's delivery stages as a step list (docs/features/02): done steps
 * collapsed, the current one open with its checklist and what is happening,
 * the rest greyed so everyone knows what's coming.
 */
export function Stages({ project }: { project: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: qk.projectStages(project), queryFn: ({ signal }) => get<ProjectStages>(`/api/projects/${enc(project)}/stages`, signal), refetchInterval: 30_000 })
  const can = useCan('project.stage', project)
  const done = (d: ProjectStages) => {
    qc.setQueryData(qk.projectStages(project), d)
    void qc.invalidateQueries({ queryKey: qk.project(project) })
  }
  const check = useMutation({ mutationFn: (b: StageCheckSet) => post<ProjectStages>(`/api/projects/${enc(project)}/stages/check`, b), onSuccess: done })
  const advance = useMutation({ mutationFn: (b: StageAdvance) => post<ProjectStages>(`/api/projects/${enc(project)}/stages/advance`, b), onSuccess: done })

  if (q.error && !q.data) return <div className="p-4"><ErrorState error={q.error} compact retry={() => void q.refetch()} /></div>
  if (!q.data) return <div className="p-4"><SkeletonRows rows={7} /></div>
  const { steps } = q.data

  return (
    <div className="flex flex-col gap-6 p-4 md:flex-row md:items-start">
    <ol className="min-w-0 flex-1">
      {steps.map((s, i) => {
        const last = i === steps.length - 1
        const open = s.checks.filter((c) => !c.done)
        return (
          <li key={s.stage} className="relative flex gap-3 pb-4">
            {!last && <span className={cn('absolute top-6 bottom-0 left-[9px] w-px', s.status === 'upcoming' ? 'bg-border' : 'bg-border-strong')} />}
            <span className="relative mt-0.5 shrink-0">
              {s.status === 'done' ? <span className="grid size-5 place-items-center rounded-full bg-ok text-bg"><Check size={12} strokeWidth={3} /></span>
                : s.status === 'skipped' ? <span className="grid size-5 place-items-center rounded-full bg-surface-3 text-fg-subtle"><SkipForward size={11} /></span>
                : s.status === 'current' ? <CircleDot size={20} className="text-accent" />
                : <Circle size={20} className="text-fg-faint" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className={cn('text-sm font-medium', s.status === 'upcoming' && 'text-fg-subtle', s.status === 'current' && 'text-fg')}>{s.label}</span>
                {s.status === 'current' && <span className="rounded-full bg-accent/15 px-1.5 text-2xs text-accent-hover">now</span>}
              </div>
              {(s.status === 'done' || s.status === 'skipped') && (
                <div className="text-2xs text-fg-faint">
                  {s.status === 'skipped' ? 'Skipped' : 'Done'}{s.by && ` by ${s.by}`} {s.at && <Age from={s.at} />}{s.reason && <> · {s.reason}</>}
                </div>
              )}
              {s.status === 'upcoming' && <div className="text-2xs text-fg-faint">{s.summary}</div>}
              {s.status === 'current' && (
                <div className="mt-1.5 rounded-md border border-border bg-surface-2 p-3">
                  <p className="text-xs text-fg-subtle">{s.summary}</p>
                  {s.checks.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {s.checks.map((c) => (
                        <li key={c.key}>
                          <label className={cn('flex items-start gap-2 text-xs', can ? 'cursor-pointer' : 'cursor-default')}>
                            <Checkbox className="mt-0.5 size-3.5" checked={c.done} disabled={!can || check.isPending}
                              onCheckedChange={(v) => check.mutate({ stage: s.stage, item: c.key, done: v === true })} />
                            <span className={cn(c.done && 'text-fg-subtle line-through')}>{c.label}</span>
                            {c.done && c.by && <span className="ml-auto shrink-0 text-2xs text-fg-faint">{c.by}</span>}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(check.error || advance.error) && <div className="mt-2"><ErrorState error={check.error ?? advance.error} compact /></div>}
                  {can && !last && (
                    <div className="mt-3 flex items-center gap-2">
                      <Button variant="primary" disabled={advance.isPending} onClick={() => {
                        if (open.length === 0) return advance.mutate({ from: s.stage })
                        void askText({ title: `Move on with ${open.length} item${open.length === 1 ? '' : 's'} open?`, body: open.map((c) => c.label).join(' · '), confirm: 'Move on' })
                          .then((reason) => { if (reason?.trim()) advance.mutate({ from: s.stage, override: true, reason: reason.trim() }) })
                      }}>Approve, move on</Button>
                      <Button disabled={advance.isPending} onClick={() => void askText({ title: `Skip ${s.label}?`, body: 'Say why. It shows on the project.', confirm: 'Skip' })
                        .then((reason) => { if (reason?.trim()) advance.mutate({ from: s.stage, skip: true, reason: reason.trim() }) })}>Skip</Button>
                      {open.length > 0 && <span className="text-2xs text-fg-faint">{open.length} open</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
    <ProjectLinks project={project} />
    </div>
  )
}

const LINK = 'flex h-8 w-full items-center gap-2 rounded-sm bg-surface-2 px-3 text-sm text-fg-muted hover:bg-hover hover:text-fg'

/** Where the project lives: one button each, only the ones that exist. */
function ProjectLinks({ project }: { project: string }) {
  const p = useProject(project).data
  const out = (href: string, icon: React.ReactNode, label: string) => (
    <a key={label} href={href} target="_blank" rel="noreferrer" className={LINK}>{icon}<span className="flex-1">{label}</span><ExternalLink size={11} className="text-fg-faint" /></a>
  )
  return (
    <nav aria-label="Project links" className="flex w-full shrink-0 flex-col gap-1.5 md:w-56">
      <Link to="/p/$project/scope" params={{ project }} className={LINK}><FileText size={14} />Scope</Link>
      <Link to="/p/$project/settings/$section" params={{ project, section: 'general' }} className={LINK}><Settings size={14} />Settings</Link>
      {p?.linearTeamId && <Link to="/p/$project/issues" params={{ project }} className={LINK}><ListTodo size={14} />Linear tickets</Link>}
      {p && out(`https://github.com/${p.repo.owner}/${p.repo.name}`, <GitBranch size={14} />, 'GitHub repo')}
      {p?.settings.uatUrl && out(p.settings.uatUrl, <Globe size={14} />, 'UAT site')}
      {p?.settings.prodUrl && out(p.settings.prodUrl, <Globe size={14} />, 'Production site')}
    </nav>
  )
}
